import type { CSSProperties, JSX } from 'react'
import { clsx } from 'clsx'
import {
  // South America
  BR, AR, UY, CO, CL, PE, EC, PY, VE, BO,
  // Europe
  FR, GB, ES, DE, PT, IT, NL, BE, HR, RS, CH, PL, DK, SE, NO, AT, UA, CZ,
  TR, GR, RU, HU, RO, SK, SI, IE, IS, FI, BA, AL, MK, GE,
  // North & Central America
  US, MX, CA, CR, PA, HN, JM, HT, CW,
  // Africa
  MA, SN, TN, DZ, EG, NG, GH, CM, CI, ZA, ML, CV,
  // Asia & Oceania
  JP, KR, AU, SA, IR, QA, AE, IQ, UZ, CN, NZ, JO, CD,
} from 'country-flag-icons/react/3x2'
import { countryIso2 } from '@/shared/lib/countryFlag'
import styles from './Flag.module.css'

type FlagSvg = (props: { title?: string; className?: string; style?: CSSProperties }) => JSX.Element

// Static map of only the nations our code table resolves to — named imports let Rollup
// tree-shake the other ~190 flags out of the bundle (a dynamic `Flags[iso2]` would not).
const FLAGS: Record<string, FlagSvg> = {
  BR, AR, UY, CO, CL, PE, EC, PY, VE, BO,
  FR, GB, ES, DE, PT, IT, NL, BE, HR, RS, CH, PL, DK, SE, NO, AT, UA, CZ,
  TR, GR, RU, HU, RO, SK, SI, IE, IS, FI, BA, AL, MK, GE,
  US, MX, CA, CR, PA, HN, JM, HT, CW,
  MA, SN, TN, DZ, EG, NG, GH, CM, CI, ZA, ML, CV,
  JP, KR, AU, SA, IR, QA, AE, IQ, UZ, CN, NZ, JO, CD,
}

type FlagProps = {
  /** National-team code — a FIFA trigram ("BRA") or an ISO-2 alpha code. */
  code: string | undefined
  /** Accessible label, typically the team name. */
  title?: string
  /** Width in px. Height stays auto to preserve the 3:2 aspect ratio. Defaults to the CSS 26px. */
  size?: number
  className?: string
}

/**
 * Renders a country flag as an inlined SVG (country-flag-icons). Unlike flag emoji,
 * these render identically on every OS — including Windows, which has no flag glyphs.
 * Falls back to a 3-letter text chip when the code has no ISO-2 mapping (e.g. clubs).
 */
export function Flag({ code, title, size, className }: FlagProps) {
  const iso2 = countryIso2(code)
  const Svg = iso2 ? FLAGS[iso2] : undefined

  // exactOptionalPropertyTypes: omit each optional prop entirely rather than pass `undefined`.
  const titleProp = title ? { title } : {}
  // Inline width beats the cross-module CSS cascade, so callers can size flags reliably.
  const styleProp = size ? { style: { width: size } } : {}

  if (!Svg) {
    return (
      <span className={clsx(styles.fallback, className)} {...titleProp} {...styleProp}>
        {code?.slice(0, 3).toUpperCase() ?? '?'}
      </span>
    )
  }

  return <Svg {...titleProp} {...styleProp} className={clsx(styles.flag, className)} />
}
